<script setup>
import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { Button } from 'laravel-nova-ui'

const DRAG_CLOSE_DISTANCE = 96
const DRAG_VELOCITY_CLOSE = 0.75

const snapHeights = {
  auto: 'max-h-[min(88dvh,42rem)]',
  half: 'h-[50dvh]',
  large: 'h-[70dvh]',
  full: 'h-[92dvh]',
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  snap: {
    type: String,
    default: 'auto',
    validator: v => ['auto', 'half', 'large', 'full'].includes(v),
  },
  dismissible: { type: Boolean, default: true },
  stackPriority: {
    type: String,
    default: 'default',
    validator: v => ['default', 'top'].includes(v),
  },
})

const emit = defineEmits(['update:modelValue'])

const sheetRef = ref(null)
const contentRef = ref(null)
const handleRef = ref(null)
const dragOffset = ref(0)
const isDragging = ref(false)
const isAnimatingDismiss = ref(false)
const useLeaveTransition = ref(true)

let dragStartY = 0
let dragStartTime = 0
let lastPointerY = 0
let lastPointerTime = 0
let dragSource = null
let contentDragStartY = 0
let contentPointerActive = false
let activeHandlePointerId = null

const sheetTransform = computed(() => {
  if (dragOffset.value <= 0) {
    return undefined
  }

  return `translateY(${dragOffset.value}px)`
})

const sheetMotionClass = computed(() => {
  if (isDragging.value || dragOffset.value <= 0) {
    return ''
  }

  return 'transition-transform duration-[260ms] ease-out'
})

const backdropZIndexClass = computed(() =>
  props.stackPriority === 'top' ? 'z-[750]' : 'z-[80]'
)

const sheetZIndexClass = computed(() =>
  props.stackPriority === 'top' ? 'z-[760]' : 'z-[90]'
)

function close() {
  emit('update:modelValue', false)
}

function resetDragState() {
  removeHandlePointerListeners()
  releaseHandlePointerCapture()
  isDragging.value = false
  isAnimatingDismiss.value = false
  dragOffset.value = 0
  dragSource = null
  contentDragStartY = 0
  contentPointerActive = false
}

function beginDrag(clientY, source) {
  if (!props.dismissible) {
    return
  }

  dragSource = source
  dragStartY = clientY
  dragStartTime = Date.now()
  lastPointerY = clientY
  lastPointerTime = dragStartTime
  isDragging.value = true
}

function updateDrag(clientY) {
  if (!isDragging.value) {
    return
  }

  dragOffset.value = Math.max(0, clientY - dragStartY)
  lastPointerY = clientY
  lastPointerTime = Date.now()
}

function endDrag() {
  if (!isDragging.value) {
    return
  }

  const distance = dragOffset.value
  const elapsed = Math.max(lastPointerTime - dragStartTime, 1)
  const velocity = (lastPointerY - dragStartY) / elapsed

  if (distance >= DRAG_CLOSE_DISTANCE || velocity >= DRAG_VELOCITY_CLOSE) {
    dismissFromDrag()
    return
  }

  isDragging.value = false
  dragOffset.value = 0
  dragSource = null
}

function dismissFromDrag() {
  const sheetHeight = sheetRef.value?.offsetHeight ?? window.innerHeight

  isDragging.value = false
  isAnimatingDismiss.value = true
  dragOffset.value = sheetHeight

  window.setTimeout(() => {
    useLeaveTransition.value = false
    close()

    nextTick(() => {
      resetDragState()
      useLeaveTransition.value = true
    })
  }, 280)
}

function removeHandlePointerListeners() {
  window.removeEventListener('pointermove', onWindowPointerMove)
  window.removeEventListener('pointerup', onWindowPointerUp)
  window.removeEventListener('pointercancel', onWindowPointerUp)
}

function onWindowPointerMove(event) {
  if (dragSource !== 'handle') {
    return
  }

  if (event.buttons === 0) {
    onWindowPointerUp()
    return
  }

  event.preventDefault()
  updateDrag(event.clientY)
}

function releaseHandlePointerCapture() {
  if (!handleRef.value || activeHandlePointerId === null) {
    return
  }

  try {
    handleRef.value.releasePointerCapture(activeHandlePointerId)
  } catch {
    //
  }

  activeHandlePointerId = null
}

function onWindowPointerUp(event) {
  removeHandlePointerListeners()
  releaseHandlePointerCapture()

  if (dragSource === 'handle') {
    endDrag()
  }

  if (event) {
    event.preventDefault()
  }
}

function onHandlePointerDown(event) {
  if (!props.dismissible) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  const handle = handleRef.value ?? event.currentTarget
  activeHandlePointerId = event.pointerId
  handle.setPointerCapture(event.pointerId)
  beginDrag(event.clientY, 'handle')

  window.addEventListener('pointermove', onWindowPointerMove, { passive: false })
  window.addEventListener('pointerup', onWindowPointerUp)
  window.addEventListener('pointercancel', onWindowPointerUp)
}

function onHandlePointerMove(event) {
  onWindowPointerMove(event)
}

function onHandlePointerUp(event) {
  onWindowPointerUp(event)
}

function onContentPointerDown(event) {
  if (!props.dismissible || !contentRef.value || contentRef.value.scrollTop > 0) {
    contentPointerActive = false
    return
  }

  contentPointerActive = true
  contentDragStartY = event.clientY
}

function onContentPointerMove(event) {
  if (!props.dismissible || !contentRef.value) {
    return
  }

  if (event.buttons === 0) {
    contentPointerActive = false

    if (isDragging.value && dragSource === 'content') {
      endDrag()
    }

    return
  }

  if (!contentPointerActive) {
    return
  }

  if (contentRef.value.scrollTop > 0) {
    if (dragSource === 'content') {
      resetDragState()
    }

    return
  }

  if (!isDragging.value) {
    const delta = event.clientY - contentDragStartY

    if (delta > 8) {
      beginDrag(contentDragStartY, 'content')
      updateDrag(event.clientY)
      event.preventDefault()
    }

    return
  }

  if (dragSource === 'content') {
    updateDrag(event.clientY)
    event.preventDefault()
  }
}

function onContentPointerUp() {
  contentPointerActive = false

  if (dragSource === 'content') {
    endDrag()
    return
  }

  contentDragStartY = 0
}

watch(
  () => props.modelValue,
  open => {
    document.body.style.overflow = open ? 'hidden' : ''

    if (open) {
      Nova.pauseShortcuts()
      resetDragState()
      return
    }

    Nova.resumeShortcuts()
    resetDragState()
  }
)

onUnmounted(() => {
  removeHandlePointerListeners()
  document.body.style.overflow = ''
  Nova.resumeShortcuts()
})
</script>

<template>
  <Teleport to="body">
    <Transition
      appear
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-250 ease-in"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="panel-bottom-sheet-backdrop fixed inset-0"
        :class="backdropZIndexClass"
        @click="dismissible ? close() : null"
      />
    </Transition>

    <Transition
      appear
      :css="useLeaveTransition"
      enter-active-class="transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
      leave-active-class="transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="modelValue"
        class="pointer-events-none fixed inset-x-0 bottom-0 flex flex-col"
        :class="[sheetZIndexClass, snapHeights[snap]]"
      >
        <div
          ref="sheetRef"
          class="panel-bottom-sheet pointer-events-auto flex min-h-0 flex-1 flex-col"
          :class="sheetMotionClass"
          :style="{
            transform: sheetTransform,
            touchAction: isDragging ? 'none' : undefined,
          }"
          @click.stop
        >
        <div class="panel-bottom-sheet-inner flex min-h-0 flex-1 flex-col">
          <div class="flex shrink-0 flex-col items-center px-5 pb-2 pt-2">
            <div
              ref="handleRef"
              class="flex w-full touch-none select-none justify-center py-3"
              :class="dismissible ? 'cursor-grab active:cursor-grabbing' : ''"
              @pointerdown="onHandlePointerDown"
              @pointermove="onHandlePointerMove"
              @pointerup="onHandlePointerUp"
              @pointercancel="onHandlePointerUp"
            >
              <div class="panel-bottom-sheet-handle h-1 w-10 rounded-full" />
            </div>

            <div
              v-if="title || $slots.header"
              class="flex w-full items-center justify-between gap-3"
            >
              <slot name="header">
                <h2 class="text-[17px] font-bold tracking-tight text-zinc-900 dark:text-white">
                  {{ title }}
                </h2>
              </slot>

              <Button
                v-if="dismissible"
                type="button"
                variant="ghost"
                icon="x-circle"
                padding="tight"
                class="panel-bottom-sheet-close"
                :aria-label="__('Close')"
                @pointerdown.stop
                @click="close"
              />
            </div>
          </div>

          <div
            ref="contentRef"
            class="panel-bottom-sheet-content min-h-0 flex-1 overflow-y-auto overscroll-contain px-5"
            @pointerdown="onContentPointerDown"
            @pointermove="onContentPointerMove"
            @pointerup="onContentPointerUp"
            @pointercancel="onContentPointerUp"
          >
            <slot />
          </div>

          <div
            v-if="$slots.footer"
            class="panel-bottom-sheet-footer shrink-0"
          >
            <slot name="footer" />
          </div>
        </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
